/**
 * <BrandPilotSharePage> - Iter 9.40 Pass D / E
 *
 * Public-ish quarterly share view for a single host's brand pilot performance.
 * Reachable at /brand-share/:attribution_code (no auth required for read).
 *
 * Backend: GET /api/brand-pilots/share/{attribution_code}
 */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2, Star, TrendingUp } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BrandPilotSharePage() {
  const { attributionCode } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/brand-pilots/share/${attributionCode}`);
        if (!res.ok) throw new Error(`Status ${res.status}`);
        setData(await res.json());
      } catch (e) {
        setError(e);
      }
      setLoading(false);
    })();
  }, [attributionCode]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50" data-testid="brand-share-loading">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50" data-testid="brand-share-error">
        <p className="text-sm text-gray-500">Unknown or expired attribution code.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20" data-testid="brand-share-page">
      <header className="bg-white border-b">
        <div className="max-w-3xl mx-auto px-4 py-6">
          <div className="flex items-center gap-2 text-xs uppercase font-semibold text-gray-500 tracking-wider">
            <Star className="w-3.5 h-3.5" /> Brand Pilot Performance · Last 90 days
          </div>
          <h1 className="text-3xl font-bold mt-2">
            <span className="capitalize">{data.brand_id}</span> × <span className="font-mono text-2xl">{data.attribution_code}</span>
          </h1>
          <div className="flex items-center gap-2 mt-2">
            <Badge variant="outline" className="capitalize">{data.tier} tier</Badge>
            <Badge className="bg-green-50 text-green-700 border-green-200" variant="outline">{data.status}</Badge>
            <span className="text-xs text-gray-400">
              as of {new Date(data.as_of).toLocaleDateString()}
            </span>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6 space-y-6">
        <div className="grid grid-cols-2 gap-3">
          <Card data-testid="brand-share-mentions">
            <CardContent className="p-5">
              <div className="text-xs uppercase font-medium text-gray-500 flex items-center gap-1.5">
                <TrendingUp className="w-3.5 h-3.5" /> Total mentions
              </div>
              <div className="text-4xl font-bold mt-2">{data.total_mentions}</div>
              <p className="text-xs text-gray-500 mt-1">Products quoted by this host</p>
            </CardContent>
          </Card>
          <Card data-testid="brand-share-products">
            <CardContent className="p-5">
              <div className="text-xs uppercase font-medium text-gray-500">Unique products</div>
              <div className="text-4xl font-bold mt-2">{data.top_products?.length || 0}</div>
              <p className="text-xs text-gray-500 mt-1">distinct SKUs attributed</p>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardContent className="p-5">
            <h3 className="text-base font-semibold mb-3">Top products</h3>
            {(data.top_products || []).length === 0 ? (
              <p className="text-sm text-gray-500">No mentions yet in this window.</p>
            ) : (
              <div className="space-y-2">
                {data.top_products.map((p) => (
                  <div key={p.product_id} className="flex items-center justify-between py-1.5 border-b last:border-b-0" data-testid={`brand-share-product-${p.product_id}`}>
                    <div className="text-sm font-medium font-mono">{p.product_id}</div>
                    <Badge variant="secondary">{p.mentions} mentions</Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <p className="text-[10px] text-gray-400 text-center">
          Phygital Brand Pilot Program - {data.host_user_id} · {data.window}
        </p>
      </main>
    </div>
  );
}
